import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from '../hooks/useBackend';

interface Region {
  region_id: number;
  carrier_region: string;
  mcc_mnc: string;
  locale: string;
  timezone_name: string;
  carrier_name: string;
  timezone_long_name: string;
}

interface CreateRegionRequest {
  carrier_region: string;
  mcc_mnc: string;
  locale: string;
  timezone_name: string;
  carrier_name: string;
  timezone_long_name: string;
}

interface RegionModalProps {
  region?: Region;
  isOpen: boolean;
  onClose: () => void;
  onSave: (region: CreateRegionRequest) => void;
  isLoading: boolean;
}

const RegionModal: React.FC<RegionModalProps> = ({ region, isOpen, onClose, onSave, isLoading }) => {
  const [formData, setFormData] = useState<CreateRegionRequest>(() => ({
    carrier_region: region?.carrier_region || '',
    mcc_mnc: region?.mcc_mnc || '',
    locale: region?.locale || '',
    timezone_name: region?.timezone_name || '',
    carrier_name: region?.carrier_name || '',
    timezone_long_name: region?.timezone_long_name || '',
  }));

  // Update form when region changes (for edit mode)
  React.useEffect(() => {
    if (region) {
      setFormData({
        carrier_region: region.carrier_region,
        mcc_mnc: region.mcc_mnc,
        locale: region.locale,
        timezone_name: region.timezone_name,
        carrier_name: region.carrier_name,
        timezone_long_name: region.timezone_long_name,
      });
    } else {
      // Reset for create mode
      setFormData({
        carrier_region: '',
        mcc_mnc: '',
        locale: '',
        timezone_name: '',
        carrier_name: '',
        timezone_long_name: '',
      });
    }
  }, [region]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof CreateRegionRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{region ? 'Edit Region' : 'Create New Region'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="region-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="carrier_region">Carrier Region *</label>
              <input
                id="carrier_region"
                type="text"
                placeholder="e.g., US, IL, SG, BG"
                value={formData.carrier_region}
                onChange={(e) => handleChange('carrier_region', e.target.value)}
                required
                maxLength={5}
              />
            </div>

            <div className="form-group">
              <label htmlFor="mcc_mnc">MCC/MNC *</label>
              <input
                id="mcc_mnc"
                type="text"
                placeholder="e.g., 310410"
                value={formData.mcc_mnc}
                onChange={(e) => handleChange('mcc_mnc', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="locale">Locale *</label>
              <input
                id="locale"
                type="text"
                placeholder="e.g., en_US"
                value={formData.locale}
                onChange={(e) => handleChange('locale', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="timezone_name">Timezone Name *</label>
              <input
                id="timezone_name"
                type="text"
                placeholder="e.g., America/New_York"
                value={formData.timezone_name}
                onChange={(e) => handleChange('timezone_name', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="carrier_name">Carrier Name *</label>
              <input
                id="carrier_name"
                type="text"
                placeholder="e.g., AT&T"
                value={formData.carrier_name}
                onChange={(e) => handleChange('carrier_name', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="timezone_long_name">Timezone Long Name *</label>
              <input
                id="timezone_long_name"
                type="text"
                placeholder="e.g., Eastern Standard Time"
                value={formData.timezone_long_name}
                onChange={(e) => handleChange('timezone_long_name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (region ? 'Update Region' : 'Create Region')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const RegionManager: React.FC = () => {
  const queryClient = useQueryClient();
  const backend = useBackend();
  
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Fetch regions
  const { data: regions = [], isLoading, error, refetch } = useQuery<Region[]>({
    queryKey: ['regions'],
    queryFn: async () => {
      const response = await fetch('/api/regions');
      if (!response.ok) throw new Error('Failed to fetch regions');
      return response.json();
    },
    refetchInterval: 30000,
  });

  // Create region mutation
  const createRegionMutation = useMutation({
    mutationFn: async (regionData: CreateRegionRequest) => {
      const response = await fetch('/api/regions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regionData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create region');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedRegion(undefined);
      queryClient.invalidateQueries({ queryKey: ['regions'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  // Update region mutation
  const updateRegionMutation = useMutation({
    mutationFn: async ({ regionId, regionData }: { regionId: number; regionData: CreateRegionRequest }) => {
      const response = await fetch(`/api/regions/${regionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regionData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update region');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsModalOpen(false);
      setSelectedRegion(undefined);
      queryClient.invalidateQueries({ queryKey: ['regions'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  // Delete region mutation
  const deleteRegionMutation = useMutation({
    mutationFn: async (regionId: number) => {
      const response = await fetch(`/api/regions/${regionId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete region');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['regions'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const handleCreateRegion = useCallback(() => {
    setSelectedRegion(undefined);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleEditRegion = useCallback((region: Region) => {
    setSelectedRegion(region);
    setIsModalOpen(true);
    setActionError(null);
  }, []);

  const handleDeleteRegion = useCallback((region: Region) => {
    if (window.confirm(`Are you sure you want to delete region "${region.carrier_region}"?`)) {
      deleteRegionMutation.mutate(region.region_id);
    }
  }, [deleteRegionMutation]);

  const handleSaveRegion = useCallback((regionData: CreateRegionRequest) => {
    if (selectedRegion) {
      updateRegionMutation.mutate({ regionId: selectedRegion.region_id, regionData });
    } else {
      createRegionMutation.mutate(regionData);
    }
  }, [selectedRegion, createRegionMutation, updateRegionMutation]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRegion(undefined);
    setActionError(null);
  }, []);

  // Convert country code to flag emoji using Unicode regional indicator symbols
  const getRegionFlag = (carrierRegion: string | undefined | null): string => {
    if (!carrierRegion || carrierRegion.length !== 2) return '🌍';
    const code = carrierRegion.toUpperCase();
    const offset = 0x1F1E6; // Regional indicator symbol letter A
    return String.fromCodePoint(
      code.charCodeAt(0) - 65 + offset,
      code.charCodeAt(1) - 65 + offset
    );
  };

  if (isLoading) return <div className="loading">Loading regions...</div>;
  if (error) return <div className="error">Error loading regions: {(error as Error).message}</div>;

  return (
    <div className="region-manager">
      <div className="page-header">
        <h1>🌍 Region Management</h1>
        <div className="page-actions">
          <button onClick={() => refetch()} className="btn-secondary">
            🔄 Refresh
          </button>
          <button onClick={handleCreateRegion} className="btn-primary">
            ➕ Add Region
          </button>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          <span>⚠️ {actionError}</span>
          <button onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      <div className="regions-grid">
        {regions.map((region) => (
          <div key={region.region_id} className="region-card">
            <div className="region-header">
              <div className="region-title">
                <span className="region-flag">{getRegionFlag(region.carrier_region)}</span>
                <h3>{region.carrier_region}</h3>
                <span className="region-carrier">{region.carrier_name}</span>
              </div>
              <div className="region-actions">
                <button 
                  onClick={() => handleEditRegion(region)}
                  className="btn-icon"
                  title="Edit region"
                >
                  ✏️
                </button>
                <button 
                  onClick={() => handleDeleteRegion(region)}
                  className="btn-icon btn-danger"
                  title="Delete region"
                >
                  🗑️
                </button>
              </div>
            </div>

            <div className="region-details">
              <div className="detail-row">
                <strong>MCC/MNC:</strong> {region.mcc_mnc}
              </div>
              <div className="detail-row">
                <strong>Locale:</strong> {region.locale}
              </div>
              <div className="detail-row">
                <strong>Timezone:</strong> {region.timezone_name}
              </div>
              <div className="detail-row">
                <strong>Full Name:</strong> {region.timezone_long_name}
              </div>
            </div>
          </div>
        ))}

        {regions.length === 0 && (
          <div className="empty-state">
            <h3>No regions configured</h3>
            <p>Add your first region to get started with bot deployment.</p>
            <button onClick={handleCreateRegion} className="btn-primary">
              ➕ Add First Region
            </button>
          </div>
        )}
      </div>

      <RegionModal
        region={selectedRegion}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveRegion}
        isLoading={createRegionMutation.isPending || updateRegionMutation.isPending}
      />
    </div>
  );
};

export default RegionManager; 